export type KeyPoseControlFrameSettings = {
  autoGenerateControlFrames: boolean;
  showControlFrames: boolean;
  showControlFrameGhosts: boolean;
};

export const KEY_POSE_CONTROL_FRAME_SETTINGS_STORAGE_KEY =
  'motionEditor:keyPoseControlFrameSettings';

export const DEFAULT_KEY_POSE_CONTROL_FRAME_SETTINGS: KeyPoseControlFrameSettings =
  {
    autoGenerateControlFrames: true,
    showControlFrames: false,
    showControlFrameGhosts: false,
  };

export function normalizeKeyPoseControlFrameSettings(
  settings: Partial<KeyPoseControlFrameSettings>,
  current: KeyPoseControlFrameSettings = DEFAULT_KEY_POSE_CONTROL_FRAME_SETTINGS,
): KeyPoseControlFrameSettings {
  const autoGenerateControlFrames =
    settings.autoGenerateControlFrames ?? current.autoGenerateControlFrames;
  const showControlFrames = autoGenerateControlFrames
    ? (settings.showControlFrames ?? current.showControlFrames)
    : false;
  const showControlFrameGhosts =
    settings.showControlFrameGhosts ?? current.showControlFrameGhosts;

  return {
    autoGenerateControlFrames,
    showControlFrames,
    showControlFrameGhosts,
  };
}

export function readKeyPoseControlFrameSettingsFromStorage():
  | KeyPoseControlFrameSettings
  | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(
      KEY_POSE_CONTROL_FRAME_SETTINGS_STORAGE_KEY,
    );
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as Partial<KeyPoseControlFrameSettings>;
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    return normalizeKeyPoseControlFrameSettings(parsed);
  } catch {
    return null;
  }
}

export function writeKeyPoseControlFrameSettingsToStorage(
  settings: KeyPoseControlFrameSettings,
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      KEY_POSE_CONTROL_FRAME_SETTINGS_STORAGE_KEY,
      JSON.stringify(settings),
    );
  } catch {
    // Ignore storage access failures.
  }
}

export function createInitialKeyPoseControlFrameSettings(): KeyPoseControlFrameSettings {
  return (
    readKeyPoseControlFrameSettingsFromStorage() ??
    DEFAULT_KEY_POSE_CONTROL_FRAME_SETTINGS
  );
}
